/**
 * SpendPill — admin-only live header pill showing today's UTC spend.
 *
 * Polls /api/admin/spend-summary every 60s. Color-shifts as it approaches
 * any configured DAILY_SPEND_CAP_USD_HYPEREAL (read from a sibling call to
 * /api/admin/spend-cap-status). Click navigates to /admin/spend.
 *
 * Renders nothing for non-admins.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DollarSign, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 60_000;

export default function SpendPill({ isAdmin }) {
  const navigate = useNavigate();
  const [todayUsd, setTodayUsd] = useState(null);
  const [capInfo, setCapInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const tick = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [s, c] = await Promise.all([
        axios.get(`${API}/api/admin/spend-summary`, { headers }),
        axios.get(`${API}/api/admin/spend-cap-status`, { headers }).catch(() => ({ data: { providers: [] } })),
      ]);
      setTodayUsd(s.data?.today?.total_usd ?? 0);
      const hyp = (c.data?.providers || []).find((p) => p.provider === 'hypereal');
      setCapInfo(hyp || null);
    } catch (e) {
      // silently fail — non-admin or backend transient — don't pollute UI
      setTodayUsd(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return undefined;
    tick();
    const iv = setInterval(tick, POLL_MS);
    return () => clearInterval(iv);
  }, [isAdmin, tick]);

  if (!isAdmin) return null;

  // Color logic: green by default, amber at >50% of any provider cap, red at >85%
  let accent = '#10B981';
  let tone = 'normal';
  if (capInfo?.cap_usd && capInfo.cap_usd > 0) {
    const ratio = (capInfo.current_usd || 0) / capInfo.cap_usd;
    if (ratio >= 0.85) { accent = '#EF4444'; tone = 'critical'; }
    else if (ratio >= 0.5) { accent = '#F59E0B'; tone = 'warning'; }
  }

  return (
    <button
      onClick={() => navigate('/admin/spend')}
      data-testid="dashboard-spend-pill"
      data-tone={tone}
      title={
        capInfo?.cap_usd
          ? `Hypereal today: $${(capInfo.current_usd ?? 0).toFixed(2)} / $${capInfo.cap_usd.toFixed(2)} cap`
          : `Click for spend dashboard · no cap configured`
      }
      className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm bg-ai-bg-secondary border border-ai-border hover:border-white/30 transition-colors text-xs font-ai"
      style={{ borderLeftColor: accent, borderLeftWidth: 2 }}
    >
      {loading ? (
        <Loader2 className="w-3 h-3 animate-spin text-white/40" />
      ) : (
        <DollarSign className="w-3 h-3" style={{ color: accent }} />
      )}
      <span className="text-ai-text-secondary uppercase tracking-wider text-[10px]">today</span>
      <span className="text-ai-text font-semibold tabular-nums">
        {todayUsd == null ? '—' : `$${todayUsd.toFixed(2)}`}
      </span>
      {capInfo?.cap_usd ? (
        <span className="text-white/30 text-[10px]">/ ${capInfo.cap_usd.toFixed(0)}</span>
      ) : null}
    </button>
  );
}
